import React from 'react';
import { MultiSelect, Option } from '../../../../../../../components/ui/multi-select';
import { CollapsibleFilterBar } from './CollapsibleFilterBar';

// Forge-specific filter constants
const FORGE_STATUS_TYPES = ['Available', 'Consumed'] as const;
const FORGE_PROJECT_TYPES = ['Production', 'PQT'] as const;

interface ForgeFilters {
  status: string[];
  nri: string[];
  orderNumber: string[];
  serialNumber: string[];
  heatNumber: string[];
  forgeNRI: string[];
  forgeProjectStage: string[];
}

interface ForgeFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: ForgeFilters;
  onFilterChange: (key: keyof ForgeFilters) => (value: string[]) => void;
  uniqueNRIs: string[];
  uniqueOrderNumbers: string[];
  uniqueSerialNumbers: string[];
  uniqueHeatNumbers: string[];
  uniqueForgeNRIs: string[];
  uniqueForgeProjectStages: string[];
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

export const ForgeFilters: React.FC<ForgeFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  uniqueNRIs,
  uniqueOrderNumbers,
  uniqueSerialNumbers,
  uniqueHeatNumbers,
  uniqueForgeNRIs,
  uniqueForgeProjectStages,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  // Convert arrays to options for MultiSelect
  const statusOptions: Option[] = FORGE_STATUS_TYPES.map(status => ({
    value: status,
    label: status
  }));

  const nriOptions: Option[] = uniqueNRIs.map(nri => ({
    value: nri,
    label: nri
  }));

  const orderNumberOptions: Option[] = uniqueOrderNumbers.map(orderNumber => ({
    value: orderNumber,
    label: orderNumber
  }));

  const serialNumberOptions: Option[] = uniqueSerialNumbers.map(serialNumber => ({
    value: serialNumber,
    label: serialNumber
  }));

  const heatNumberOptions: Option[] = uniqueHeatNumbers.map(heatNumber => ({
    value: heatNumber,
    label: heatNumber
  }));

  const forgeNRIOptions: Option[] = uniqueForgeNRIs.map(forgeNRI => ({
    value: forgeNRI,
    label: forgeNRI
  }));

  const forgeProjectStageOptions: Option[] = FORGE_PROJECT_TYPES.map(forgeProjectStage => ({
    value: forgeProjectStage,
    label: forgeProjectStage
  }));

  const activeFilterCount =
    filters.status.length +
    filters.orderNumber.length +
    filters.serialNumber.length +
    filters.heatNumber.length +
    filters.forgeNRI.length +
    filters.forgeProjectStage.length;

  return (
    <CollapsibleFilterBar
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search materials by NRI, order number, or details..."
      activeFilterCount={activeFilterCount}
      filteredCount={filteredCount}
      filteredCountLabel="materials"
      onResetFilters={onResetFilters}
    >
      <MultiSelect
        options={statusOptions}
        selected={filters.status}
        onChange={onFilterChange('status')}
        placeholder="Status"
        isSearchable={false}
      />
      <MultiSelect
        options={orderNumberOptions}
        selected={filters.orderNumber}
        onChange={onFilterChange('orderNumber')}
        placeholder="Order Number"
      />
      <MultiSelect
        options={serialNumberOptions}
        selected={filters.serialNumber}
        onChange={onFilterChange('serialNumber')}
        placeholder="Serial Number"
      />
      <MultiSelect
        options={heatNumberOptions}
        selected={filters.heatNumber}
        onChange={onFilterChange('heatNumber')}
        placeholder="Heat Number"
      />
      <MultiSelect
        options={forgeNRIOptions}
        selected={filters.forgeNRI}
        onChange={onFilterChange('forgeNRI')}
        placeholder="Forge NRI"
      />
      <MultiSelect
        options={forgeProjectStageOptions}
        selected={filters.forgeProjectStage}
        onChange={onFilterChange('forgeProjectStage')}
        placeholder="Stage"
      />
    </CollapsibleFilterBar>
  );
}; 